import { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Send, Plus, Trash2, Sparkles, MessageSquare } from "lucide-react";

export default function Assistant() {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const bottomRef = useRef(null);

  const loadSessions = async () => {
    const r = await api.get("/assistant/sessions");
    setSessions(r.data);
    return r.data;
  };

  const openSession = async (session) => {
    setActiveSession(session);
    const r = await api.get(`/assistant/sessions/${session.id}/messages`);
    setMessages(r.data);
  };

  useEffect(() => {
    loadSessions().then((list) => {
      if (list.length > 0) openSession(list[0]);
    });
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamText]);

  const newSession = async () => {
    const r = await api.post("/assistant/sessions");
    const updated = await loadSessions();
    const created = updated.find((s) => s.id === r.data.id) || r.data;
    setActiveSession(created);
    setMessages([]);
  };

  const deleteSession = async (id) => {
    await api.delete(`/assistant/sessions/${id}`);
    const updated = await loadSessions();
    if (activeSession?.id === id) {
      if (updated.length > 0) openSession(updated[0]);
      else { setActiveSession(null); setMessages([]); }
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || streaming) return;
    let session = activeSession;
    if (!session) {
      const r = await api.post("/assistant/sessions");
      session = r.data;
      setActiveSession(session);
    }
    const userText = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: "user", content: userText }]);
    setStreaming(true);
    setStreamText("");

    try {
      const res = await fetch(`${API}/assistant/sessions/${session.id}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: userText }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.error) {
            toast.error(payload.error);
          } else if (payload.delta) {
            full += payload.delta;
            setStreamText(full);
          } else if (payload.done) {
            setMessages((prev) => [...prev, { id: `assistant-${Date.now()}`, role: "assistant", content: full }]);
            setStreamText("");
          }
        }
      }
      loadSessions();
    } catch (e) {
      toast.error("Assistant is temporarily unavailable");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <DashboardLayout title="AI Assistant">
      <div className="grid grid-cols-[240px_1fr] gap-4 h-[calc(100vh-160px)]" data-testid="assistant-page">
        <div className="bg-white border border-[#E2E8F0] rounded-lg flex flex-col overflow-hidden">
          <div className="p-3 border-b border-[#E2E8F0]">
            <Button size="sm" data-testid="assistant-new-chat-button" onClick={newSession} className="w-full bg-[#76C121] hover:bg-[#5C9819] text-white">
              <Plus className="h-3.5 w-3.5 mr-1" /> New Chat
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessions.map((s) => (
              <div
                key={s.id}
                data-testid={`assistant-session-${s.id}`}
                onClick={() => openSession(s)}
                className={`group flex items-center justify-between gap-2 px-2.5 py-2 rounded-md cursor-pointer text-sm transition-colors duration-200 ${activeSession?.id === s.id ? "bg-[#76C121]/10 text-[#00823B]" : "text-[#4B5563] hover:bg-[#F8F9FA]"}`}
              >
                <span className="truncate flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5 shrink-0" /> {s.title}</span>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`assistant-delete-session-${s.id}`} onClick={(e) => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 text-red-500 shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this chat?</AlertDialogTitle>
                      <AlertDialogDescription>This conversation will be permanently removed.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteSession(s.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="assistant-messages">
            {messages.length === 0 && !streamText && (
              <div className="h-full flex flex-col items-center justify-center text-center text-[#4B5563]">
                <Sparkles className="h-8 w-8 text-[#76C121] mb-3" />
                <p className="text-sm font-medium text-[#1C1C1E]">Ask me about our Knowledge Hub or SOP Library</p>
                <p className="text-xs mt-1 max-w-xs">e.g. "What's our client onboarding process?" or "Which SOPs cover data governance?"</p>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`max-w-[75%] ${m.role === "user" ? "ml-auto text-right" : ""}`}>
                <div className={`inline-block px-4 py-2.5 rounded-lg text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#76C121]/10 text-[#1C1C1E]" : "bg-[#F8F9FA] text-[#1C1C1E]"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {streamText && (
              <div className="max-w-[75%]">
                <div className="inline-block px-4 py-2.5 rounded-lg text-sm whitespace-pre-wrap bg-[#F8F9FA] text-[#1C1C1E]">{streamText}</div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-[#E2E8F0] p-4 flex gap-2">
            <Textarea
              data-testid="assistant-message-input"
              placeholder="Ask the assistant..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              rows={1}
              className="resize-none"
              disabled={streaming}
            />
            <Button data-testid="assistant-send-button" onClick={sendMessage} disabled={streaming || !input.trim()} className="bg-[#76C121] hover:bg-[#5C9819] text-white shrink-0">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
